import { DOCUMENT } from '@angular/common';
import { Injectable, OnDestroy, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CurrentUserService } from '../../../common/current-user/current-user.service';
import { FirebaseService } from '../../../common/firebase/firebase.service';
import {
  BriefAudienceType,
  BriefStatusEnum,
  CreateBriefDto,
  DeliveryTypeEnum,
  InfluencerTypeEnum,
  SubmittedPostVersionEnum,
  VideoFormatEnum,
} from '../../../common/general-types/briefs.dto';
import { GptCompletionMessage } from '../../../common/general-types/open-ai.dto';
import { CreateProductDto, UgcProductResponseDto } from '../../../common/general-types/products.dto';
import { GaService } from '../../../services/ga/ga';
import {
  ONBOARDING_FLOW,
  QUICK_BRIEF_FAILURE_EVENTS,
  onboardingStepIndex,
} from '../../../services/ga/onboarding.analytics';
import { ChatGptService } from '../../../services/chat-gpt/chat-gpt.service';
import { ImageService } from '../../../services/image/image.service';
import { PricingModelV2Service } from '../../../services/pricing-model-v2/pricing-model-v2.service';
import { ProductService } from '../../../services/product/product.service';
import { BriefService } from '../../../services/brief/brief.service';
import { CreateBriefService } from '../../briefs/service/create-brief/create-brief.service';
import { readDraft } from '../../onboarding-value-first/services/value-first-onboarding.types';
import {
  GeneratedQuickBrief,
  QUICK_BRIEF_NETWORKS,
  QUICK_BRIEF_TIMELINE,
  QuickBriefFocus,
  QuickBriefFormat,
  QuickBriefNetworkId,
  QuickBriefProduct,
  QuickBriefStep,
  channelPlan,
  conceptRecords,
  deliverableLines,
  displayDomain,
  listHtml,
  manualProductError,
  networkNames,
  normaliseProductUrl,
  parseBriefJson,
  parseGeneratedBrief,
  previousQuestion,
  productUrlError,
  progressFor,
  stepsHtml,
  thinkingStageLabels,
} from './quick-brief.types';

const STAGE_MS = 1200;
const FAST_STAGE_MS = 160;
const GENERATION_ERROR = 'We could not generate a usable brief. Please try again.';
const PRODUCT_FETCH_ERROR = "We couldn't read that page. Check the link or enter the product details.";

interface PricingSlice {
  id: string;
  thirtySecPrice?: number;
  ugcPrice?: number;
  socialPostPrice?: number;
  advertisingPrice?: number;
  commissionPercentage?: number;
  licenseCommissionPercentage?: number;
  currencyCode?: string;
}

interface ScrapedProduct {
  productName?: string;
  productDescription?: string;
  productPrice?: string | number;
  currency?: string;
  productImage?: string;
}

@Injectable()
export class QuickBriefService implements OnDestroy {
  readonly networks = QUICK_BRIEF_NETWORKS;
  readonly timeline = QUICK_BRIEF_TIMELINE;

  readonly step = signal<QuickBriefStep>('focus');
  readonly focus = signal<QuickBriefFocus | null>(null);
  readonly format = signal<QuickBriefFormat | null>(null);
  readonly selectedNetworks = signal<QuickBriefNetworkId[]>([]);
  readonly productUrl = signal('');
  readonly productLoading = signal(false);
  readonly product = signal<QuickBriefProduct | null>(null);
  readonly manualEntry = signal(false);
  readonly manual = signal<QuickBriefProduct>(emptyProduct());
  readonly error = signal('');
  readonly thinkStage = signal(0);
  readonly rewriting = signal(false);
  readonly version = signal(1);
  readonly brief = signal<GeneratedQuickBrief | null>(null);
  readonly saving = signal(false);
  /** True while Accept & publish, or Publish it now, is talking to the API. */
  readonly publishing = signal(false);
  readonly objective = signal('Brand Awareness');
  readonly brandName = signal('Your brand');
  readonly domain = signal('');
  readonly brandLogo = signal('');

  readonly progress = computed(() => progressFor(this.step(), this.format()));
  readonly showSummary = computed(() => this.step() === 'focus' || this.step() === 'format' || this.step() === 'network');
  readonly showBack = computed(() => this.step() !== 'thinking' && this.step() !== 'published' && this.step() !== 'draft');
  readonly continueLabel = computed(() => {
    if (this.step() === 'network' || (this.step() === 'format' && this.format() === 'ugc')) {
      return 'Generate my brief';
    }
    return 'Continue';
  });
  readonly summaryPromoting = computed(() => {
    if (this.focus() === 'product') {
      return this.product()?.name || this.manual().name || '';
    }
    if (this.focus() === 'brand') {
      return this.brandName();
    }
    return '';
  });
  readonly summaryContent = computed(() => {
    if (this.format() === 'ugc') {
      return 'UGC only';
    }
    if (this.format() === 'influencer') {
      return 'Influencer posts';
    }
    return '';
  });
  readonly summaryChannels = computed(() => {
    if (this.format() === 'ugc') {
      return 'Not needed for UGC';
    }
    return networkNames(this.selectedNetworks());
  });
  readonly deliverables = computed(() => deliverableLines(this.format(), this.selectedNetworks()));
  readonly stageLabels = computed(() => thinkingStageLabels({
    domain: this.domain(),
    subject: this.focus() === 'product' ? (this.product()?.name || 'product') : this.brandName(),
    brandName: this.brandName(),
    format: this.format(),
    focus: this.focus(),
    networkLabel: networkNames(this.selectedNetworks()),
  }));
  readonly insightChips = computed(() => {
    const stage = this.thinkStage();
    const chips: string[] = [];
    if (stage >= 1 && this.domain()) {
      chips.push(`Site: ${this.domain()}`);
    }
    if (stage >= 2) {
      chips.push(`Goal: ${this.objective()}`);
    }
    if (stage >= 3 && this.summaryPromoting()) {
      chips.push(`Hero: ${this.summaryPromoting()}`);
    }
    if (stage >= 4 && this.brief()) {
      chips.push('Concepts: 3 drafted');
    }
    if (stage >= 5) {
      chips.push('Creators: matched to your brief');
    }
    return chips;
  });

  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly currentUser = inject(CurrentUserService);
  private readonly firebase = inject(FirebaseService);
  private readonly products = inject(ProductService);
  private readonly chat = inject(ChatGptService);
  private readonly pricing = inject(PricingModelV2Service);
  private readonly briefs = inject(BriefService);
  private readonly createBrief = inject(CreateBriefService);
  private readonly ga = inject(GaService);
  readonly images = inject(ImageService);

  private persistedProduct: UgcProductResponseDto | null = null;
  private savedBriefId: string | null = null;
  private generationDone = false;
  private destroyed = false;
  private stageTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    const brand = this.currentUser.focussedBrand as {
      companyName?: string;
      website?: string;
      companyLogo?: string;
    } | undefined;
    if (brand?.companyName) {
      this.brandName.set(brand.companyName);
    }
    this.domain.set(displayDomain(brand?.website));
    this.brandLogo.set(brand?.companyLogo ?? '');
    const draft = readDraft(this.document.defaultView?.sessionStorage);
    const goal = draft?.goals.find((item) => item.trim().length > 0);
    if (goal) {
      this.objective.set(goal);
    }
    this.trackStep(this.step());
  }

  private setStep(next: QuickBriefStep): void {
    if (this.step() === next) {
      return;
    }
    this.step.set(next);
    if (!this.destroyed) {
      this.trackStep(next);
    }
  }

  private trackStep(step: QuickBriefStep): void {
    this.track('onboarding_step_view', {
      step,
      step_index: onboardingStepIndex(ONBOARDING_FLOW.quickBrief, step),
    });
  }

  private track(event: string, properties?: Record<string, unknown>): void {
    this.ga.trackOnboardingEvent(event, {
      flow: ONBOARDING_FLOW.quickBrief,
      ...properties,
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.clearStageTimer();
  }

  imageSrc(url: string): string {
    return this.images.getImageSource(url, 'gallery');
  }

  networkSelected(id: QuickBriefNetworkId): boolean {
    return this.selectedNetworks().includes(id);
  }

  channelPlan() {
    return channelPlan(this.selectedNetworks());
  }

  selectFocus(next: QuickBriefFocus): void {
    this.focus.set(next);
    this.error.set('');
  }

  setProductUrl(value: string): void {
    this.productUrl.set(value);
    this.error.set('');
  }

  selectFormat(next: QuickBriefFormat): void {
    this.format.set(next);
    this.error.set('');
    if (next === 'ugc') {
      this.selectedNetworks.set([]);
    }
  }

  toggleNetwork(id: QuickBriefNetworkId): void {
    this.error.set('');
    this.selectedNetworks.update((current) => (
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    ));
  }

  setManualField(field: keyof QuickBriefProduct, value: string): void {
    this.manual.update((current) => ({ ...current, [field]: value }));
    this.error.set('');
  }

  setManualImage(url: string): void {
    this.setManualField('imageUrl', url);
  }

  changeProduct(): void {
    this.product.set(null);
    this.persistedProduct = null;
    this.manualEntry.set(false);
    this.manual.set(emptyProduct(this.currencyCode()));
    this.error.set('');
  }

  async fetchProduct(): Promise<boolean> {
    const message = productUrlError(this.productUrl());
    if (message) {
      this.error.set(message);
      return false;
    }
    this.productLoading.set(true);
    this.error.set('');
    try {
      const response = await firstValueFrom(
        this.products.getProductDetails(normaliseProductUrl(this.productUrl()), this.brandName()),
      ) as ScrapedProduct | null;
      if (this.destroyed) {
        return false;
      }
      if (!response?.productName) {
        this.openManual(response);
        return false;
      }
      this.product.set(productFromScrape(response, normaliseProductUrl(this.productUrl())));
      this.manualEntry.set(false);
      this.persistedProduct = null;
      return true;
    } catch {
      if (!this.destroyed) {
        this.openManual(null);
      }
      return false;
    } finally {
      this.productLoading.set(false);
    }
  }

  async continueFromFocus(): Promise<void> {
    if (this.productLoading()) {
      return;
    }
    if (!this.focus()) {
      this.error.set('Choose what you want to promote.');
      return;
    }
    if (this.focus() === 'product') {
      const ready = await this.ensureProductAnswer();
      if (!ready) {
        return;
      }
    }
    this.error.set('');
    this.setStep('format');
  }

  async continueFromFormat(): Promise<void> {
    if (!this.format()) {
      this.error.set('Choose a content type.');
      return;
    }
    this.error.set('');
    if (this.format() === 'ugc') {
      await this.generate(false);
      return;
    }
    this.setStep('network');
  }

  async continueFromNetwork(): Promise<void> {
    if (this.selectedNetworks().length === 0) {
      this.error.set('Choose at least one network.');
      return;
    }
    this.error.set('');
    await this.generate(false);
  }

  async regenerate(): Promise<void> {
    this.version.update((current) => current + 1);
    const ok = await this.generate(true);
    if (!ok) {
      this.version.update((current) => Math.max(1, current - 1));
    }
  }

  async accept(): Promise<void> {
    this.publishing.set(true);
    try {
      const saved = await this.save(BriefStatusEnum.Public);
      if (saved && !this.destroyed) {
        this.setStep('published');
      }
    } finally {
      if (!this.destroyed) {
        this.publishing.set(false);
      }
    }
  }

  async saveDraft(): Promise<void> {
    const saved = await this.save(BriefStatusEnum.Draft);
    if (saved) {
      this.setStep('draft');
    }
  }

  async publishNow(): Promise<void> {
    if (!this.savedBriefId || this.saving()) {
      return;
    }
    this.publishing.set(true);
    this.saving.set(true);
    this.error.set('');
    try {
      await firstValueFrom(this.briefs.publicBrief(this.savedBriefId));
      if (!this.destroyed) {
        this.setStep('published');
      }
    } catch {
      if (!this.destroyed) {
        this.error.set('We could not publish your brief. Please try again.');
      }
    } finally {
      this.saving.set(false);
      if (!this.destroyed) {
        this.publishing.set(false);
      }
    }
  }

  goBack(): void {
    if (this.step() === 'focus') {
      void this.router.navigate(['/onboarding-select']);
      return;
    }
    const previous = previousQuestion(this.step(), this.format());
    if (!previous) {
      return;
    }
    this.error.set('');
    this.setStep(previous);
  }

  goToDashboard(): void {
    this.currentUser.onboardingComplete.set(true);
    this.currentUser.checkBrandComplete();
    void this.router.navigate(['/dashboard']);
  }

  private async ensureProductAnswer(): Promise<boolean> {
    if (this.manualEntry()) {
      const draft = { ...this.manual(), sourceUrl: normaliseProductUrl(this.productUrl()) };
      const message = manualProductError(draft);
      if (message) {
        this.error.set(message);
        return false;
      }
      this.product.set(draft);
      this.persistedProduct = null;
      return true;
    }
    if (this.product()) {
      return true;
    }
    const fetched = await this.fetchProduct();
    if (fetched) {
      return true;
    }
    return false;
  }

  private openManual(response: ScrapedProduct | null): void {
    this.track(QUICK_BRIEF_FAILURE_EVENTS.manualFallback);
    this.manualEntry.set(true);
    this.product.set(null);
    this.persistedProduct = null;
    this.manual.set({
      name: response?.productName ?? '',
      description: response?.productDescription ?? '',
      price: response?.productPrice != null ? String(response.productPrice) : '',
      currency: (response?.currency || this.currencyCode()).toUpperCase(),
      imageUrl: response?.productImage ?? '',
      sourceUrl: normaliseProductUrl(this.productUrl()),
    });
    this.error.set(PRODUCT_FETCH_ERROR);
  }

  private async generate(rewrite: boolean): Promise<boolean> {
    const previous = this.brief();
    this.rewriting.set(rewrite);
    this.setStep('thinking');
    this.thinkStage.set(0);
    this.generationDone = false;
    this.error.set('');
    this.playStages();
    try {
      const product = await this.ensurePersistedProduct();
      const generated = await this.requestBrief(product);
      if (this.destroyed) {
        return false;
      }
      this.persistedProduct = product;
      this.brief.set(generated);
      this.generationDone = true;
      if (this.thinkStage() >= 5) {
        this.setStep('review');
      }
      return true;
    } catch {
      if (!this.destroyed) {
        this.clearStageTimer();
        this.brief.set(previous);
        this.generationDone = false;
        this.track(QUICK_BRIEF_FAILURE_EVENTS.generationFailed);
        this.setStep(previous ? 'review' : (this.format() === 'influencer' ? 'network' : 'format'));
        this.error.set(GENERATION_ERROR);
      }
      return false;
    }
  }

  private playStages(): void {
    this.clearStageTimer();
    if (this.destroyed) {
      return;
    }
    if (this.thinkStage() >= 5) {
      if (this.generationDone && this.brief()) {
        this.setStep('review');
      }
      return;
    }
    this.stageTimer = setTimeout(() => {
      this.thinkStage.update((current) => current + 1);
      this.playStages();
    }, this.generationDone ? FAST_STAGE_MS : STAGE_MS);
  }

  private clearStageTimer(): void {
    if (this.stageTimer) {
      clearTimeout(this.stageTimer);
      this.stageTimer = null;
    }
  }

  private async ensurePersistedProduct(): Promise<UgcProductResponseDto> {
    if (this.persistedProduct?.id) {
      return this.persistedProduct;
    }
    if (this.focus() === 'brand') {
      return this.loadBrandProduct();
    }
    const draft = this.product();
    if (!draft) {
      throw new Error('Product is missing');
    }
    return this.createProduct(draft, 0);
  }

  private async loadBrandProduct(): Promise<UgcProductResponseDto> {
    const brandId = this.brandId();
    const products = await firstValueFrom(this.products.getbusinessProducts(brandId)) as UgcProductResponseDto[];
    const existing = products?.find((item) => item.productType === 4)
      ?? products?.find((item) => item.productCategory === 'Brand');
    if (existing) {
      return existing;
    }
    const name = this.brandName();
    return this.createProduct({
      name,
      description: `${name} is a brand that sells ${name} products.`,
      price: '0',
      currency: this.currencyCode(),
      imageUrl: this.brandLogo(),
      sourceUrl: this.brandWebsite(),
    }, 4);
  }

  private async createProduct(draft: QuickBriefProduct, productType: number): Promise<UgcProductResponseDto> {
    const price = Number(draft.price.trim());
    const body: CreateProductDto = {
      productType,
      productImage: draft.imageUrl,
      productName: draft.name,
      productDescription: draft.description,
      productValue: Number.isFinite(price) ? Math.round(price * 100) : 0,
      currency: draft.currency.toUpperCase(),
      website: draft.sourceUrl || this.brandWebsite(),
      accessInstructions: productType === 4 ? 'Visit the brand website to learn more about the brand.' : '',
      creatorId: this.firebase.userId ?? '',
      businessAccountId: this.brandId(),
      productCategory: productType === 4 ? 'Brand' : '',
      productIndustry: productType === 4 ? 'Brand' : '',
    };
    return firstValueFrom(this.products.createUserProduct(body));
  }

  private async requestBrief(product: UgcProductResponseDto): Promise<GeneratedQuickBrief> {
    const messages = this.briefMessages(product);
    let generated = await this.completeBrief(messages);
    if (!generated) {
      generated = await this.completeBrief([
        ...messages,
        {
          role: 'user',
          content: 'Your previous reply was not the required JSON. Reply again with only that JSON object.',
        },
      ]);
    }
    if (!generated) {
      throw new Error('Brief generation failed');
    }
    return generated;
  }

  private async completeBrief(messages: GptCompletionMessage[]): Promise<GeneratedQuickBrief | null> {
    const response = await firstValueFrom(this.chat.sendCompletionMessage(messages)) as {
      choices?: { message?: { content?: string } }[];
    };
    const raw = response?.choices?.[0]?.message?.content;
    if (!raw) {
      return null;
    }
    try {
      return parseGeneratedBrief(parseBriefJson(raw));
    } catch {
      return null;
    }
  }

  private briefMessages(product: UgcProductResponseDto): GptCompletionMessage[] {
    const brand = this.currentUser.focussedBrand as { companyName?: string; about?: string } | undefined;
    const channels = this.format() === 'ugc' ? 'UGC only. Creators film content for the brand.' : networkNames(this.selectedNetworks());
    const lines = deliverableLines(this.format(), this.selectedNetworks());
    return [
      {
        role: 'system',
        content: `You write creator briefs. Reply with only one JSON object and no markdown.
Keys: title, summary, keyMessages, concepts, dos, donts, creatorProfile, niches.
keyMessages, dos, donts, and niches are arrays of plain strings.
concepts is an array of exactly 3 objects with name, hook, and description.
title is a short campaign title. summary is one paragraph.`,
      },
      {
        role: 'user',
        content: `Brand: ${brand?.companyName || this.brandName()}
Brand description: ${brand?.about || ''}
Domain: ${this.domain()}
Promoting: ${product.productName}
Product description: ${product.productDescription}
Objective: ${this.objective()}
Content: ${this.summaryContent()}
Channels: ${channels}
Deliverables to reflect: ${lines.join('; ')}
Timeline: ${QUICK_BRIEF_TIMELINE}
Write three concepts and a creator profile. Do not invent a budget.`,
      },
    ];
  }

  private async save(status: BriefStatusEnum): Promise<boolean> {
    const generated = this.brief();
    const product = this.persistedProduct;
    if (!generated || !product?.id || this.saving()) {
      return false;
    }
    this.saving.set(true);
    this.error.set('');
    try {
      const models = await firstValueFrom(
        this.pricing.getCustomPricingModels(this.brandId(), this.currencyCode().toLowerCase()),
      ) as PricingSlice[];
      const model = models[0];
      if (!model) {
        throw new Error('Pricing model missing');
      }
      const plan = this.format() === 'influencer' ? channelPlan(this.selectedNetworks()) : null;
      const created = await firstValueFrom(
        this.createBrief.saveNewBrief(this.buildDto(generated, product.id, model, status, plan)),
      );
      if (this.destroyed) {
        return false;
      }
      this.savedBriefId = created?.id ?? null;
      this.track('onboarding_quick_brief_created', {
        status: status === BriefStatusEnum.Public ? 'public' : 'draft',
        focus: this.focus() ?? '',
        format: this.format() ?? '',
        networks: this.selectedNetworks().join(','),
      });
      return true;
    } catch {
      if (!this.destroyed) {
        this.track(QUICK_BRIEF_FAILURE_EVENTS.saveFailed);
        this.error.set('We could not save your brief. Please try again.');
      }
      return false;
    } finally {
      this.saving.set(false);
    }
  }

  private buildDto(
    generated: GeneratedQuickBrief,
    productId: string,
    model: PricingSlice,
    status: BriefStatusEnum,
    plan: ReturnType<typeof channelPlan>,
  ): CreateBriefDto {
    const ugc = this.format() === 'ugc';
    const deliverables = this.deliverables();
    return {
      campaignId: '',
      productId,
      title: generated.title,
      stepsHtml: stepsHtml({
        summary: generated.summary,
        keyMessages: generated.keyMessages,
        deliverables,
        creatorProfile: generated.creatorProfile,
        niches: generated.niches,
      }),
      doHtml: listHtml(generated.dos),
      dontHtml: listHtml(generated.donts),
      creativeConcepts: conceptRecords(generated.concepts),
      contentPillars: [],
      description: generated.summary,
      basePricingModelId: model.id,
      videoFormat: VideoFormatEnum.Vertical,
      videoPointers: [],
      requiredCreators: 1,
      videosPerCreator: 1,
      businessAccountId: this.brandId(),
      sendNotStartedNotifications: true,
      noAutoPayment: false,
      deliveryType: DeliveryTypeEnum.none,
      demographics: {
        gender: [],
        interests: [],
        location: [],
        age: [],
        categoryId: 3,
        subCategoryId: 54,
        countryCodes: [],
        is18plus: false,
        influencerType: [InfluencerTypeEnum.Any],
        followerRanges: [],
      },
      briefPricing: {
        fifteenSecPrice: 0,
        thirtySecPrice: model.thirtySecPrice || 0,
        sixtySecPrice: 0,
        facePrice: 0,
        ugcPrice: ugc ? model.ugcPrice || 0 : 0,
        advertisingPrice: 0,
        socialPostPrice: !ugc && plan ? model.socialPostPrice || 0 : 0,
        commissionPercentage: model.commissionPercentage || 0,
        licenseCommissionPercentage: model.licenseCommissionPercentage || 0,
        partnerContribution: 0,
        currencyCode: (model.currencyCode || this.currencyCode()).toLowerCase(),
        ranks: [{ rank: 0, multiplier: 1 }],
        createdBy: this.firebase.userId || '',
        influencerLevels: [{ title: 'All Sizes', bonusValue: 0, commission: 0, followersTo: 100000000, followersFrom: 0 }],
      },
      status,
      enableNoodle: true,
      humanInLoop: false,
      brief_audience: BriefAudienceType.ALL,
      brief_payment_method_id: null,
      brief_auto_config_id: null,
      is_appless_brief: false,
      creator_persona: undefined,
      brief_goal: { name: this.objective(), description: this.objective() },
      complianceDocIds: undefined,
      enableAffiliateMarketing: false,
      isPublic: status === BriefStatusEnum.Public,
      shoutBrief: plan ? {
        callToActionLink: undefined,
        suggestedCaption: undefined,
        suggestedHastags: undefined,
        socialPlatform: plan.primary,
        resultsVersion: SubmittedPostVersionEnum.SuperSocial,
        crossPlatforms: plan.cross,
      } : undefined,
    };
  }

  private brandId(): string {
    const id = (this.currentUser.focussedBrand as { id?: string } | undefined)?.id;
    if (!id) {
      throw new Error('Brand is missing');
    }
    return id;
  }

  private brandWebsite(): string {
    const website = (this.currentUser.focussedBrand as { website?: string } | undefined)?.website;
    return website?.trim() || this.domain();
  }

  private currencyCode(): string {
    const code = (this.currentUser.focussedBrand as { currencyCode?: string } | undefined)?.currencyCode;
    return (code || 'gbp').toUpperCase();
  }
}

function emptyProduct(currency = ''): QuickBriefProduct {
  return { name: '', description: '', price: '', currency, imageUrl: '', sourceUrl: '' };
}

function productFromScrape(response: ScrapedProduct, sourceUrl: string): QuickBriefProduct {
  return {
    name: response.productName ?? '',
    description: response.productDescription ?? '',
    price: response.productPrice != null ? String(response.productPrice) : '',
    currency: (response.currency || '').toUpperCase(),
    imageUrl: response.productImage ?? '',
    sourceUrl,
  };
}
